import * as React from 'react'

import { Building2, User } from 'lucide-react'
import { MobXProviderContext } from 'mobx-react'

import {
  Avatar,
  AvatarFallback,
  AvatarImage,
} from '~/client/src/shared/components/shadcn-ui/avatar'
import { Toggle } from '~/client/src/shared/components/shadcn-ui/toggle'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '~/client/src/shared/components/shadcn-ui/tooltip'
import Localization from '~/client/src/shared/localization/LocalizationManager'
import UserProject from '~/client/src/shared/models/UserProject'

export interface IMeCompanyQuickFiltersProps {
  isOnlyMe?: boolean
  isOnlyMyCompany?: boolean
  onToggleMe?: () => void
  onToggleMyCompany?: () => void
  isMeDisabled?: boolean
  isMyCompanyDisabled?: boolean
  showMe?: boolean
  showMyCompany?: boolean
  size?: React.ComponentProps<typeof Toggle>['size']
  iconOnly?: boolean
  /**
   * The company logo, for hosts without zoomin's project stores (the login
   * bundle's User Portal). Omit it to read the current project's company.
   */
  myCompanyLogoUrl?: string
}

// Per the design: the user's photo cropped round (an avatar), the company's
// logo shown whole (not an avatar); the bare glyph when there is no image.
const UserPhoto: React.FC<{ src?: string }> = ({ src }) =>
  src ? (
    <Avatar className="tw:size-4" aria-hidden="true">
      <AvatarImage src={src} alt="" />
      <AvatarFallback>
        <User />
      </AvatarFallback>
    </Avatar>
  ) : (
    <User aria-hidden="true" />
  )

const CompanyLogo: React.FC<{ src?: string }> = ({ src }) => {
  // A URL that fails to load would otherwise leave the toggle empty.
  const [failedSrc, setFailedSrc] = React.useState<string>()
  return src && src !== failedSrc ? (
    <img
      src={src}
      alt=""
      aria-hidden="true"
      className="tw:size-4 tw:object-contain"
      onError={() => setFailedSrc(src)}
    />
  ) : (
    <Building2 aria-hidden="true" />
  )
}

// The trigger is a span, not the Toggle: Toggle doesn't forward a ref, and a
// disabled Toggle is pointer-events-none, so it could never open the tooltip.
const QuickFilterTooltip: React.FC<{ text: string; enabled: boolean }> = ({
  text,
  enabled,
  children,
}) =>
  enabled ? (
    <Tooltip>
      <TooltipTrigger render={<span className="tw:inline-flex tw:shrink-0" />}>
        {children}
      </TooltipTrigger>
      <TooltipContent>{text}</TooltipContent>
    </Tooltip>
  ) : (
    <>{children}</>
  )

const MeCompanyQuickFilters: React.FC<IMeCompanyQuickFiltersProps> = ({
  isOnlyMe,
  isOnlyMyCompany,
  onToggleMe,
  onToggleMyCompany,
  isMeDisabled,
  isMyCompanyDisabled,
  showMe = !!onToggleMe,
  showMyCompany = !!onToggleMyCompany,
  size,
  iconOnly,
  myCompanyLogoUrl,
}) => {
  const stores = React.useContext(MobXProviderContext) as any
  const currentUser = stores.state?.user
  // The login bundle has no user or project stores; getCompanyId would throw.
  const companyId =
    myCompanyLogoUrl === undefined && currentUser && stores.userProjectsStore
      ? UserProject.getCompanyId(currentUser, stores.userProjectsStore)
      : null
  const companyAvatarUrl =
    myCompanyLogoUrl ??
    (companyId
      ? stores.companiesStore?.getCompanyById(companyId)?.avatarUrl
      : undefined)
  // Unlike Button, Toggle can shrink, and its explicit min width lets a tight
  // row squeeze it below its label, which then spills past the border.
  const toggleClassName = iconOnly ? 'tw:shrink-0 tw:px-0' : 'tw:shrink-0'

  return (
    <>
      {showMe && (
        <QuickFilterTooltip
          text={Localization.translator.filterByMe}
          enabled={!!iconOnly}
        >
          <Toggle
            variant="outline"
            size={size}
            pressed={isOnlyMe}
            onPressedChange={onToggleMe}
            disabled={isMeDisabled}
            aria-label={iconOnly ? Localization.translator.me : undefined}
            className={toggleClassName}
          >
            <UserPhoto src={currentUser?.avatarUrl} />
            {!iconOnly && Localization.translator.me}
          </Toggle>
        </QuickFilterTooltip>
      )}
      {showMyCompany && (
        <QuickFilterTooltip
          text={Localization.translator.filterByMyCompany}
          enabled={!!iconOnly}
        >
          <Toggle
            variant="outline"
            size={size}
            pressed={isOnlyMyCompany}
            onPressedChange={onToggleMyCompany}
            disabled={isMyCompanyDisabled}
            aria-label={iconOnly ? Localization.translator.myCo : undefined}
            className={toggleClassName}
          >
            <CompanyLogo src={companyAvatarUrl} />
            {!iconOnly && Localization.translator.myCo}
          </Toggle>
        </QuickFilterTooltip>
      )}
    </>
  )
}

export default MeCompanyQuickFilters
